import { emitEvent } from "./emit-event";
import { EVENT_TYPE } from "./events";
import { saveUtmParams } from "./utilities";

const Channel = {
  affiliates: "Affiliates",
  direct: "Direct",
  display: "Display",
  email: "Email",
  organicSearch: "Organic Search",
  paidSearch: "Paid Search",
  paidSocial: "Paid Social",
  referral: "Referral",
  social: "Social"
} as const;

const Medium = {
  affiliate: "affiliate",
  banner: "banner",
  cpc: "cpc",
  display: "display",
  email: "email",
  none: "(none)",
  organic: "organic",
  paid: "paid",
  paidSearch: "paid search",
  paidSocial: "paid social",
  referral: "referral",
  social: "social"
} as const;

export const initializeTrafficSourceTracker = () => {
  type Channel = (typeof Channel)[keyof typeof Channel];
  type Medium = (typeof Medium)[keyof typeof Medium];

  const referrer = document.referrer;

  // Make sure to only run this script if the current page load is from a different origin, ie externally referred traffic.
  // Basically it should only fire once when the user first lands on the site, not on every page view.
  if (referrer !== document.location.origin) {
    // Get URL parameters and convert keys to lowercase
    const originalParams = new URLSearchParams(window.location.search);
    const urlParams = new URLSearchParams();

    for (const [key, value] of originalParams.entries()) {
      urlParams.set(key.toLowerCase(), value);
    }

    const referrerHostname = referrer ? new URL(referrer).hostname.toLowerCase() : "";
    const currentHostname = window.location.hostname.toLowerCase();

    let source = "";
    let medium: Medium;
    let defaultChannel: Channel;

    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const gadSource = urlParams.get("gad_source");
    const gclid = urlParams.get("gclid");
    const gbraid = urlParams.get("gbraid");

    // 1. Prioritize UTM Parameters
    if (utmSource && utmMedium) {
      source = utmSource;
      medium = utmMedium as Medium;

      // Determine default channel based on UTM medium
      const mediumLower = utmMedium.toLowerCase();

      if (mediumLower.includes("social") && mediumLower.includes("paid")) {
        defaultChannel = Channel.paidSocial;
      } else if (
        mediumLower.includes("cpc") ||
        mediumLower.includes("ppc") ||
        mediumLower.includes("paid")
      ) {
        defaultChannel = Channel.paidSearch;
      } else if (mediumLower.includes("social")) {
        defaultChannel = Channel.social;
      } else if (mediumLower.includes("email")) {
        defaultChannel = Channel.email;
      } else if (mediumLower.includes("display") || mediumLower.includes("banner")) {
        defaultChannel = Channel.display;
      } else if (mediumLower.includes("affiliate")) {
        defaultChannel = Channel.affiliates;
      } else if (mediumLower.includes("organic")) {
        defaultChannel = Channel.organicSearch;
      } else {
        defaultChannel = Channel.direct;
      }
    }
    // 2. Handle Google Paid Traffic (when no UTMs)
    else if (gadSource || gclid || (referrerHostname.includes("google") && gbraid)) {
      source = "google";
      medium = Medium.cpc;
      defaultChannel = Channel.paidSearch;
    }
    // 3. Handle Google Organic Traffic (when no UTMs)
    else if (referrerHostname.includes("google")) {
      source = "google";
      medium = Medium.organic;
      defaultChannel = Channel.organicSearch;
    }
    // 4. Handle Other Search Engines (when no UTMs)
    else if (
      referrerHostname.includes("bing") ||
      referrerHostname.includes("yahoo") ||
      referrerHostname.includes("duckduckgo") ||
      referrerHostname.includes("yandex") ||
      referrerHostname.includes("baidu")
    ) {
      source = referrerHostname;
      medium = Medium.organic;
      defaultChannel = Channel.organicSearch;
    }
    // 5. Handle Social Media Traffic (when no UTMs)
    else if (
      referrerHostname.includes("facebook") ||
      referrerHostname.includes("fb") ||
      referrerHostname.includes("instagram") ||
      referrerHostname.includes("ig") ||
      referrerHostname.includes("twitter") ||
      referrerHostname.includes("x.com") ||
      referrerHostname.includes("linkedin") ||
      referrerHostname.includes("pinterest") ||
      referrerHostname.includes("tiktok") ||
      referrerHostname.includes("youtube") ||
      referrerHostname.includes("snapchat")
    ) {
      source = referrerHostname;

      // Check for paid social indicators
      if (
        urlParams.get("fbclid") ||
        urlParams.get("igshid") ||
        urlParams.get("twclid") ||
        urlParams.get("li_fat_id")
      ) {
        medium = Medium.paidSocial;
        defaultChannel = Channel.paidSocial;
      } else {
        medium = Medium.social;
        defaultChannel = Channel.social;
      }
    }
    // 6. Handle Display/Affiliate Traffic (when no UTMs)
    else if (
      urlParams.get("dclid") ||
      urlParams.get("msclkid") ||
      urlParams.get("aff_id") ||
      urlParams.get("affiliate_id")
    ) {
      if (urlParams.get("aff_id") || urlParams.get("affiliate_id")) {
        source = "affiliate";
        medium = Medium.affiliate;
        defaultChannel = Channel.affiliates;
      } else {
        source = "display network";
        medium = Medium.display;
        defaultChannel = Channel.display;
      }
    }
    // 7. Handle Email Traffic (when no UTMs)
    else if (
      referrerHostname.includes("gmail") ||
      referrerHostname.includes("outlook") ||
      (referrerHostname.includes("yahoo") && referrerHostname.includes("mail")) ||
      urlParams.get("email_id") ||
      urlParams.get("em_id")
    ) {
      source = referrerHostname || "email";
      medium = Medium.email;
      defaultChannel = Channel.email;
    }
    // 8. Handle External Links / Referrals (when no UTMs or other specific matches)
    else if (referrerHostname !== "" && referrerHostname !== currentHostname) {
      source = referrerHostname;
      medium = Medium.referral;
      defaultChannel = Channel.referral;
    }
    // 9. Handle Direct Traffic (when nothing else matches)
    else {
      source = "(direct)";
      medium = Medium.none;
      defaultChannel = Channel.direct;
    }

    // Emit the traffic source event
    emitEvent(
      EVENT_TYPE.trafficSource,
      {
        // Legacy camelCase naming standard, kept for backwards compatibility
        defaultChannel,

        default_channel: defaultChannel,
        medium,
        source
      },
      false
    );

    saveUtmParams({ channel: defaultChannel, medium, source });
  }
};
